<template>
  <main>
    <form-errors :errors="formErrors" />
    <div v-if="paymentMethodStep === INPUT_STEP.SELECT_METHOD">
      <div class="tdesk-form">
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <span style="font-weight: 600;">
                You are purchasing {{ numberOfEnrolsToPurchase }} {{ enrolmentsPluralized }} into {{ courseTitle }} for {{ totalAsDollarValue }}.
              </span>
            </div>
            <div class="form-group required">
              <label>Total</label>
              <input
                id="price"
                v-model="total"
                type="number"
                :disabled="true"
                required
              >
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-group required">
              <label for="">Payment Method</label>
              <select id="payment-methods" v-model="paymentMethod">
                <option value="">
                  Select Payment Method
                </option>
                <option value="credit-card">
                  Credit Card
                </option>
                <option v-if="courseCanBeInvoiced" value="invoice-client">
                  {{ invoiceOptionText }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <button
        id="proceed"
        class="tdesk-primary-button"
        :disabled="!paymentMethod"
        @click="handleProceedToPaymentConfirmation()"
      >
        <i class="fas fa-arrow-circle-right" />
        Proceed
      </button>
    </div>

    <div v-if="paymentMethodStep === INPUT_STEP.SELECT_INVOICE && paymentMethod === 'credit-card'" id="select-invoice" class="tdesk-form">
      <h4 style="margin-top: 5px;">
        Who is making this payment?
      </h4>
      <div>
        <div class="form-group radio">
          <input
            id="self-pay"
            v-model="invoiceToType"
            type="radio"
            value="myself"
          >
          <label for="myself">Myself</label>
        </div>
        <div class="form-group radio">
          <input
            id="other-pay"
            v-model="invoiceToType"
            type="radio"
            value="other"
          >
          <label for="other">Other (e.g., Company)</label>
        </div>

        <div v-if="invoiceToType === 'other'" class="form-group required">
          <input
            id="payer-name"
            v-model="invoiceTo.last_name_or_care_of"
            type="text"
            required
            placeholder="Holding Company LTD"
          >
        </div>

        <button
          id="confirm-payer"
          class="tdesk-primary-button"
          :disabled="!invoiceToType || (invoiceToType === 'other' && !invoiceTo.last_name_or_care_of)"
          @click="handleProceedToPayment"
        >
          <i class="fas fa-arrow-circle-right" />
          Proceed To Payment
        </button>
        <button
          class="tdesk-secondary-button"
          @click="paymentMethodStep = INPUT_STEP.SELECT_METHOD"
        >
          Go Back
        </button>
      </div>
    </div>

    <div v-if="paymentMethodStep === INPUT_STEP.INPUT_CREDIT_CARD && paymentMethod === 'credit-card'" id="credit-card" class="tdesk-form">
      <stripe
        v-bind="stripeProps"
        :key="0"
        :show-back-button="true"
        @go-back="paymentMethodStep = INPUT_STEP.SELECT_INVOICE"
        @setPaymentIntent="paymentIntentId = $event"
        @paymentComplete="onPaymentComplete"
      />
    </div>

    <div v-if="paymentMethodStep === INPUT_STEP.CONFIRM_INVOICE_CLIENT && paymentMethod === 'invoice-client'" id="invoice_client">
      <invoice-client-form
        :partnership-name="parsedUserInformation.invoice_to_partnership_name"
        :client-name="parsedUserInformation.client_name"
        :client-user-full-name="parsedUserInformation.client_user_full_name"
        :client-user-id="parsedUserInformation.client_user_id"
        :partner-id="partnerId"
        :course-id="courseId"
        :participants="participants"
        :organizer="organizer"
        :price-per-enrol="pricePerEnrol"
        @confirm-invoice-client="handleProceedToPayment"
        @paymentComplete="onPaymentComplete"
        @back="paymentMethodStep = INPUT_STEP.SELECT_METHOD"
      />
    </div>
  </main>
</template>

<script>
import Stripe from '../../../components/stripe.vue';
import FormErrors from "../../../components/form_errors.vue";
import InvoiceClientForm from './InvoiceClientForm.vue';

export default {
  components: {
    FormErrors,
    Stripe,
    InvoiceClientForm,
  },
  props: {
    pricePerEnrol: { type: Number, required: true },
    participants: { type: Array, required: true },
    organizer: { type: Object, required: true },
    courseTitle: { type: String, required: true },
    courseId: { type: Number, required: true },
    isPartnerCourse: { type: Boolean, required: true },
    partnerId: { type: Number, required: false, default: null },
    stripeApiKey: { type: String, required: true },
    stripeAccountId: { type: String, required: false, default: null },
    currentUserInformation: { type: String, required: true },
  },
  data() {
    return {
      INPUT_STEP: {
        SELECT_METHOD: 'SELECT_METHOD',
        SELECT_INVOICE: 'SELECT_INVOICE',
        INPUT_CREDIT_CARD: 'INPUT_CREDIT_CARD',
        CONFIRM_INVOICE_CLIENT: 'CONFIRM_INVOICE_CLIENT',
      },
      paymentMethod: '',
      formErrors: [],
      paymentMethodStep: 'SELECT_METHOD',
      paymentIntentId: null,
      invoiceToType: '',
      clientInvoiceType: '',
      invoiceTo: { last_name_or_care_of: null, first_name: null },
      parsedUserInformation: JSON.parse(this.currentUserInformation),
    };
  },
  computed: {
    totalAsDollarValue() {
      return `$${(this.total).toFixed(2)}`;
    },
    total() {
      return this.numberOfEnrolsToPurchase * this.pricePerEnrol;
    },
    numberOfEnrolsToPurchase() {
      return this.participants.length;
    },
    invoiceOptionText() {
      return `Request Invoice for ${this.parsedUserInformation.client_name}`;
    },
    enrolmentsPluralized() {
      return this.numberOfEnrolsToPurchase > 1 ? 'enrolments' : 'enrolment';
    },
    correctStripeAccountId() {
      return this.isPartnerCourse ? this.stripeAccountId : undefined;
    },
    courseCanBeInvoiced() {
      return this.isPartnerCourse && this.clientCanInvoiceCourse;
    },
    clientCanInvoiceCourse() {
      return this.parsedUserInformation.user_can_invoice && this.parsedUserInformation.invoice_to_partnership_id === this.partnerId;
    },
    paymentIntentPath() {
      if (this.isPartnerCourse) {
        return Routes.partner_create_multi_user_payment_learner_partner_lms_course_path({
          course_id: this.courseId,
          user_information: this.participants,
          organizer_information: this.organizer,
          partnership_id: this.partnerId,
        });
      } else {
        return Routes.learner_lms_courses_multi_user_payment_index_path({
          course_id: this.courseId,
          user_information: this.participants,
          organizer_information: this.organizer,
          invoice_to: this.invoiceTo,
        });
      }
    },
    stripeProps() {
      return {
        fullSize: true,
        paymentIntentCreatePath: this.paymentIntentPath,
        apiKey: this.stripeApiKey,
        hideTitle: true,
        stripeAccountId: this.correctStripeAccountId,
        price: this.totalAsDollarValue,
        storedPaymentMethods: [],
        hideSummary: true,
        purchaseParams: {
          amount_in_cents: this.total * 100,
          invoice_to: this.invoiceTo,
        },
      };
    },
  },
  watch: {
    invoiceToType(newType) {
      if (newType === 'myself') {
        this.invoiceTo.last_name_or_care_of = this.organizer.last_name;
        this.invoiceTo.first_name = this.organizer.first_name;
      } else if (newType === 'other') {
        this.invoiceTo.last_name_or_care_of = '';
        this.invoiceTo.first_name = '';
      }
    },
  },
  methods: {
    onPaymentComplete(message) {
      this.paymentMethodStep = this.INPUT_STEP.SELECT_METHOD;
      this.$emit('paymentComplete', message);
    },
    handleProceedToPayment() {
      if (this.paymentMethod === 'credit-card') {
        this.paymentMethodStep = this.INPUT_STEP.INPUT_CREDIT_CARD;
      }
    },
    handleProceedToPaymentConfirmation() {
      if (this.paymentMethod === 'credit-card') {
        this.paymentMethodStep = this.INPUT_STEP.SELECT_INVOICE;
      } else if (this.paymentMethod === 'invoice-client') {
        this.paymentMethodStep = this.INPUT_STEP.CONFIRM_INVOICE_CLIENT;
      }
    },
  },
};
</script>
